import { belongsToHomeworkTab } from '../utils/workKind';
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { NEXT_PUBLIC_API_BASE_URL } from '@env';
import { Logger } from '../utils/logger';
import { getApiAccessToken, clearTokenCache } from './tokenManager';
import {
  CreateCxcRegistrationRequest,
  CreateEarlyPickupRequest,
  CxcRegistration,
  CxcRegistrationReceipt,
  CxcOptions, CxcSubject,
  EarlyPickupCollector,
  EarlyPickupRequest,
  EarlyPickupRequestPage,
  EarlyPickupStatus,
  CatalogItem,
  DocumentForm,
  ParentAdmissionDocument,
  ParentDocument,
  ParentRequestDetail,
  ParentRequestPage,
  ParentRequestSummary,
  ParentRequestView,
  PickedDocumentFile,
  ResubmitDocumentRequestInput,
  SubmitDocumentRequestInput,
} from '../types';
import type { ParentTransportCard, ParentTransportToday } from '../types/transport';
import type { ReportCardDocument, ReportCardListItem } from '../types/reportCard';

// Both web and native call the host configured in .env's NEXT_PUBLIC_API_BASE_URL
// directly — no platform branching, no dev-server proxy indirection.
const BASE_URL = `${NEXT_PUBLIC_API_BASE_URL}/api`;

console.log('📋 [API] Configuration Loaded:');
console.log(`  NEXT_PUBLIC_API_BASE_URL: ${NEXT_PUBLIC_API_BASE_URL}`);
console.log(`  BASE_URL (with /api): ${BASE_URL}`);

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Attach request start time so we can compute duration in the response interceptor
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _startTime?: number;
  }
}

// Rebuilds the URL the way it actually goes out — path + query string — so the
// console shows `/attendance/...?year=2026&month=8` instead of just the path.
// Axios keeps `params` separate from `url`, which hid them from every log line.
function loggedUrl(config: { url?: string; params?: Record<string, unknown> }): string {
  const url = config.url ?? '';
  if (!config.params) return url;
  try {
    const qs = Object.entries(config.params)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join(',') : String(v)}`)
      .join('&');
    return qs ? `${url}?${qs}` : url;
  } catch {
    return url;
  }
}

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const token = await getApiAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        // Who is calling, for the school's last-login record ("ParentApp/android").
        config.headers['X-Client-Platform'] = `ParentApp/${Platform.OS}`;
      } else {
        console.log('⚠️  [API] No token available for request');
      }
    } catch (error) {
      Logger.warn('API', 'Failed to get access token', error);
      console.warn('⚠️  [API] Token acquisition failed:', error);
    }
    config._startTime = Date.now();
    // Request body is logged here (dev only); the response interceptor logs the
    // response body — together they give the full request/response pair per call.
    Logger.api(
      config.method ?? 'GET',
      loggedUrl(config),
      undefined,
      undefined,
      __DEV__ && config.data ? config.data : undefined,
    );
    return config;
  },
  error => {
    Logger.error('API', 'Request setup failed', error);
    console.error('❌ [API] Request setup error:', error);
    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  response => {
    const duration = response.config._startTime
      ? Date.now() - response.config._startTime
      : undefined;
    Logger.api(
      response.config.method ?? 'GET',
      loggedUrl(response.config),
      response.status,
      duration,
      __DEV__ ? response.data : undefined,
    );
    return response;
  },
  async (error: AxiosError) => {
    const duration = error.config?._startTime
      ? Date.now() - error.config._startTime
      : undefined;

    Logger.api(
      error.config?.method ?? 'GET',
      error.config ? loggedUrl(error.config) : '',
      error.response?.status,
      duration,
      __DEV__ ? error.response?.data : undefined,
    );

    console.log('❌ [API] Request failed:', {
      method: error.config?.method,
      url: error.config ? loggedUrl(error.config) : undefined,
      status: error.response?.status,
      statusText: error.response?.statusText,
      duration: `${duration}ms`,
      errorMessage: error.message,
      responseData: __DEV__ ? error.response?.data : 'Check __DEV__ mode',
    });

    if (error.response?.status === 401) {
      Logger.warn('API', 'Token rejected by server — clearing local session and token cache');
      console.warn('🔐 [API] 401 Unauthorized - Token may be invalid or expired');
      await clearTokenCache();
      await AsyncStorage.multiRemove(['user', 'selectedStudent']);
    }

    if (!error.response) {
      // No response at all means the backend is unreachable (down, wrong URL,
      // CORS, DNS...) — this must NOT be silently swallowed as "no data", or a
      // real outage looks identical to an empty result everywhere in the app.
      Logger.error('API', `Network error — backend unreachable at ${error.config?.url}`, error);
      console.error('🌐 [API] Network error - Backend unreachable:', {
        url: error.config?.url,
        errorMessage: error.message,
      });
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export const studentAPI = {
  // Returns a StudentFullSnapshotDto[] (Profile + Enrollments + Relatives, etc.) per child.
  // /Parent/portal, not /Parent: only logins the school invited get children (owner, 28 Oct 2026);
  // anyone else is refused with a `reason` (see utils/portalAccess.ts).
  getMyChildren: async () => {
    try {
      console.log('🔍 [API] Calling /api/Parent/portal endpoint...');
      const response = await apiClient.get('/Parent/portal');
      console.log('✅ [API] /Parent/portal response received:', {
        status: response.status,
        dataLength: Array.isArray(response.data) ? response.data.length : 'not array',
        firstItem: Array.isArray(response.data) ? response.data[0] : response.data,
      });
      return response;
    } catch (error: any) {
      console.error('❌ [API] /Parent/portal endpoint FAILED:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        data: error.response?.data,
      });
      throw error;
    }
  },
  getStudentProfile: (studentId: string) => apiClient.get(`/student/${studentId}`),
  getStudentDocumentBlob: (studentId: string, documentId: string) =>
    apiClient.get(`/StudentDocument/profile/${studentId}/download/${documentId}`, { responseType: 'blob' }),

  // The dedicated photo pipeline (StudentPhotoController) - see useStudentProfilePhoto.ts for
  // why this replaced the StudentDocument-based "Passport Size Photo" lookup.
  describeStudentPhoto: (studentId: string) => apiClient.get(`/student/${studentId}/photo`),
  getStudentPhotoBlob: (studentId: string, version: string, thumbnail = false) =>
    apiClient.get(`/student/${studentId}/photo/${version}`, {
      params: thumbnail ? { thumbnail: true } : undefined,
      responseType: 'blob',
    }),
  // formData must contain a 'file' field (and optionally 'thumbnail'), matching
  // UploadStudentPhotoRequest. Content-Type stays undefined for the multipart boundary,
  // same as uploadStudentDocumentByProfile below.
  uploadStudentPhoto: (studentId: string, formData: FormData) =>
    apiClient.post(`/student/${studentId}/photo`, formData, {
      headers: { 'Content-Type': undefined as any },
    }),
  getStudentPerformanceSummary: (studentId: string, academicYear: number, todayDateInt: number) =>
    apiClient.get(`/Student/${studentId}/performance/summary`, {
      params: { academicYear, todayDateInt }
    }),
  updateStudentProfile: (studentId: string, payload: any) =>
    apiClient.post(`/Student/profile/${studentId}/true`, payload),
  // Master list of document types available to upload (name, code, allowedFileTypes, allowedFileSize, etc.)
  // stage narrows the list: ENROLLED for current students, ADMISSION for applicants.
  getDocumentTypes: (stage?: string) =>
    apiClient.get('/StudentDocument/list', { params: stage ? { stage } : undefined }),

  // Upload keyed on the student profile - the path for CURRENT students, who
  // have no application to reference. Content-Type stays undefined for the
  // multipart boundary, same as uploadStudentDocument below.
  uploadStudentDocumentByProfile: (docId: string, studentProfileId: string, formData: FormData) =>
    apiClient.post(`/StudentDocument/upload/doc/${docId}/profile/${studentProfileId}`, formData, {
      headers: { 'Content-Type': undefined as any },
    }),
  // formData must contain a 'File' field. Content-Type is left undefined so axios/browser
  // can auto-generate the multipart boundary — setting it manually here would break parsing.
  uploadStudentDocument: (docId: string, applicationId: string, formData: FormData) =>
    apiClient.post(`/StudentDocument/upload/doc/${docId}/application/${applicationId}`, formData, {
      headers: { 'Content-Type': undefined as any },
    }),
};

export const attendanceAPI = {
  // Get attendance overview for a specific student in a specific month
  // Includes: Monthly summary + Today's status + Weekly breakdown
  // Endpoint: /student/attendance/student/{studentId}/class/{classId}?year={year}&month={month}
  // Returns: StudentMonthlyAttendanceCalendarDto with:
  //   - totalWorkingDays, totalPresentDays, totalAbsentDays, totalLeaveDays, attendancePercentage
  //   - days: List of all days in month with status
  //   - today: Today's attendance status (if marked)
  //   - weeklyBreakdown: This week's daily attendance breakdown
  getAttendanceOverview: (studentId: string, classId: string, year: number, month: number) =>
    apiClient.get(
      `/student/attendance/student/${studentId}/class/${classId}`,
      { params: { year, month } }
    ),

  // Alias for backward compatibility
  getAttendance: (studentId: string, classId: string, year: number, month: number) =>
    apiClient.get(
      `/student/attendance/student/${studentId}/class/${classId}`,
      { params: { year, month } }
    ),

  // One student's per-subject attendance percentages, till date — same endpoint
  // the staff web frontend uses (subjectAttendanceService.getStudentSubjectAttendanceSummary).
  // Returns SubjectAttendanceStudentSummary[]: subjectName, sessionsHeld,
  // presentCount, absentCount, presencePercent.
  getSubjectAttendanceSummary: (studentId: string, academicYear?: number) =>
    apiClient.get(`/school/subject-attendance/student/${studentId}/summary`, {
      params: academicYear ? { academicYear } : undefined,
    }),

  // The registers this school takes each day, in order: one for a single daily
  // register, two (morning, afternoon) for a school taking two. Used to show a
  // sitting that has not been taken yet today as "Not marked yet".
  // Returns AttendanceSessionDto[]: code, label, ordinal, startTime, endTime.
  getAttendanceSessions: () => apiClient.get('/student/attendance/sessions'),
};

export const leaveAPI = {
  getLeaveRequests: (studentId: string) => apiClient.get(`/studentleave/${studentId}`),

  applyLeave: (data: { studentId: string; fromDate: string; toDate: string; reason: string }) =>
    apiClient.post('/studentleave', {
      StudentId: data.studentId,
      StartDate: data.fromDate,
      EndDate: data.toDate,
      Reason: data.reason,
    }),

  // No cancel/delete endpoint exists on the backend — the Cancel action is disabled
  // in LeavesScreen until one is added.
};

// CXC/CSEC registration - the Cayman Academy form for an enrolled pupil. The
// backend re-checks that studentId is one of THIS parent's children and answers
// 404 otherwise, so a wrong id is indistinguishable from "no such pupil".
export const cxcRegistrationAPI = {
  // The form's subject grid, staff-maintained; CA is this school's own list.
  getSubjects: () =>
    apiClient.get<CxcSubject[]>('/CxcSubject/active', { params: { form: 'CA' } }),

  // Sittings, deadlines and fees - the server's one copy, also what it checks the submit against.
  getOptions: () =>
    apiClient.get<CxcOptions>('/CxcSettings/options', { params: { form: 'CA' } }),

  submit: (data: CreateCxcRegistrationRequest) =>
    apiClient.post<CxcRegistrationReceipt>('/CxcRegistration/parent', data),

  getMyChildRegistrations: (studentId: string) =>
    apiClient.get<CxcRegistration[]>(`/CxcRegistration/parent/${studentId}`),
};

// Early pickup requests. Unlike leaveAPI's /studentleave, this controller is
// caller-scoped: /my returns the requests THIS parent raised across all their
// children, so studentId is a filter rather than a required path segment.
export const earlyPickupAPI = {
  createRequest: (data: CreateEarlyPickupRequest) =>
    apiClient.post<EarlyPickupRequest>('/early-pickup-requests', {
      studentId: data.studentId,
      pickupDate: data.pickupDate,
      requestedPickupTime: data.requestedPickupTime,
      reasonCode: data.reasonCode,
      reasonText: data.reasonText ?? null,
      collectorContactId: data.collectorContactId,
      notes: data.notes ?? null,
    }),

  getMyRequests: (params?: {
    status?: EarlyPickupStatus;
    studentId?: string;
    fromDate?: string;
    toDate?: string;
    page?: number;
    pageSize?: number;
  }) =>
    apiClient.get<EarlyPickupRequestPage>('/early-pickup-requests/my', {
      params: { page: 1, pageSize: 50, ...params },
    }),

  getRequest: (id: string) =>
    apiClient.get<EarlyPickupRequest>(`/early-pickup-requests/${id}`),

  // The school's approved collector list for this child — the pickup person must be
  // chosen from here, never typed in.
  getCollectors: (studentId: string) =>
    apiClient.get<EarlyPickupCollector[]>(
      `/early-pickup-requests/students/${studentId}/collectors`,
    ),

  // The server rejects this unless the request is still PENDING or APPROVED, which is
  // the same rule its canCancel flag reports — the UI gates on the flag, not on a copy
  // of the rule.
  cancelRequest: (id: string, reason?: string) =>
    apiClient.post<EarlyPickupRequest>(`/early-pickup-requests/${id}/cancel`, {
      reason: reason?.trim() ? reason.trim() : null,
    }),
};

// Adds one chosen file as a multipart part. The browser hands back a real File;
// React Native's FormData instead streams the {uri, name, type} shape from disk.
function appendPickedFile(formData: FormData, part: string, picked: PickedDocumentFile) {
  if (Platform.OS === 'web' && picked.file) {
    formData.append(part, picked.file as any);
    return;
  }
  formData.append(part, { uri: picked.uri, name: picked.name, type: picked.type } as any);
}

// Answers travel as one JSON object part ("values") and each attachment as its own
// file_{fieldKey} part - the shape ParentDocumentsController reads for both submit
// and resubmit.
function documentRequestForm(
  values: Record<string, string>,
  files: Record<string, PickedDocumentFile>,
  extra: Record<string, string>,
): FormData {
  const formData = new FormData();
  Object.entries(extra).forEach(([key, value]) => formData.append(key, value));
  formData.append('values', JSON.stringify(values));
  Object.entries(files).forEach(([fieldKey, picked]) =>
    appendPickedFile(formData, `file_${fieldKey}`, picked),
  );
  return formData;
}

// Student documents (certificates, bonafide, transfer certificate...). Every route is
// scoped by the server to the caller's own children; another family's id reads as 404.
export const documentsAPI = {
  // The catalogue for one child, with eligibility and why a document cannot be requested.
  getCatalog: (studentId: string) =>
    apiClient.get<CatalogItem[]>('/parent/document-types', { params: { studentId } }),

  getForm: (documentTypeId: string, studentId: string) =>
    apiClient.get<DocumentForm>(`/parent/document-types/${documentTypeId}`, {
      params: { studentId },
    }),

  // Multipart; Content-Type stays undefined so the boundary is generated for us.
  submit: (input: SubmitDocumentRequestInput) =>
    apiClient.post<ParentRequestSummary>(
      '/parent/document-requests',
      documentRequestForm(input.values, input.files, {
        studentId: input.studentId,
        documentTypeId: input.documentTypeId,
        configVersion: String(input.configVersion),
        confirmed: input.confirmed ? 'true' : 'false',
      }),
      { headers: { 'Content-Type': undefined as any } },
    ),

  // Family-wide when studentId is omitted. counts covers every view whatever the filter.
  getRequests: (params: {
    studentId?: string;
    view?: ParentRequestView;
    page?: number;
    pageSize?: number;
  }) =>
    apiClient.get<ParentRequestPage>('/parent/document-requests', {
      params: {
        studentId: params.studentId || undefined,
        view: params.view || undefined,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
      },
    }),

  getRequest: (id: string) =>
    apiClient.get<ParentRequestDetail>(`/parent/document-requests/${id}`),

  resubmit: (id: string, input: ResubmitDocumentRequestInput) =>
    apiClient.post<ParentRequestDetail>(
      `/parent/document-requests/${id}/resubmit`,
      documentRequestForm(
        input.values,
        input.files,
        input.note?.trim() ? { note: input.note.trim() } : {},
      ),
      { headers: { 'Content-Type': undefined as any } },
    ),

  cancel: (id: string, reason?: string) =>
    apiClient.post<ParentRequestDetail>(`/parent/document-requests/${id}/cancel`, {
      reason: reason?.trim() ? reason.trim() : null,
    }),

  getAttachment: (requestId: string, attachmentId: string) =>
    apiClient.get(`/parent/document-requests/${requestId}/attachments/${attachmentId}`, {
      responseType: 'blob',
    }),

  // The family document locker. Pass studentId to show one child.
  getDocuments: (studentId?: string) =>
    apiClient.get<ParentDocument[]>('/parent/documents', {
      params: { studentId: studentId || undefined },
    }),

  // The issued PDF. The first download marks the request DELIVERED on the server.
  download: (documentId: string) =>
    apiClient.get(`/parent/documents/${documentId}/download`, { responseType: 'blob' }),

  // What the family uploaded at admission. Pass studentId to show one child.
  getAdmissionDocuments: (studentId?: string) =>
    apiClient.get<ParentAdmissionDocument[]>('/parent/admission-documents', {
      params: { studentId: studentId || undefined },
    }),

  downloadAdmissionDocument: (documentId: string) =>
    apiClient.get(`/parent/admission-documents/${documentId}/download`, { responseType: 'blob' }),
};

export const homeworkAPI = {
  // enrollmentId (selectedStudent.enrollmentId) personalises each item's
  // status with THAT child's hand-in state (Submitted/Graded); without it the
  // status is derived client-side from the due date only.
  getHomeworkByClass: (classId: string, startDate?: string, endDate?: string, enrollmentId?: string) =>
    apiClient.get(`/homework/parent/class/${classId}`, {
      params: { startDate, endDate, enrollmentId },
    }),

  /**
   * Everything a family should see under "Homework", from BOTH places a
   * teacher can set it.
   *
   * The school has two modules that both put work in front of a child: the
   * legacy Home Work screen (the Homework table) and the merged New Assessment
   * form (the Assessment table, with a category). Only the first reached this
   * tab, so homework set the newer way simply never arrived.
   *
   * Assessments whose category is NOT homework - tests, quizzes, exams - are
   * returned separately for the Assessments tab rather than mixed in here.
   * A failure on either side is not allowed to empty the other: one module
   * being down should cost a section, not the screen.
   */
  getHomeworkFeed: async (
    classId: string, startDate?: string, endDate?: string, enrollmentId?: string,
  ): Promise<{ homework: any[]; otherAssessments: any[] }> => {
    const [homeworkResult, assessmentResult] = await Promise.allSettled([
      apiClient.get(`/homework/parent/class/${classId}`, {
        params: { startDate, endDate, enrollmentId },
      }),
      assessmentAPI.getAssignedByClass(classId, startDate, endDate, enrollmentId),
    ]);

    const homework: any[] = homeworkResult.status === 'fulfilled'
      ? homeworkResult.value.data?.data ?? []
      : [];

    const assessments: any[] = assessmentResult.status === 'fulfilled'
      ? assessmentResult.value.data?.data ?? []
      : [];

    // One definition, shared with the Assessments tab - see utils/workKind.
    const isHomework = belongsToHomeworkTab;

    // Each row is tagged with the module it came from. The two live in
    // different tables, and only Homework rows can be handed in -
    // HomeworkSubmission/submit resolves the id against the Homework table, so
    // posting an assessment id there returns "Homework not found." Without this
    // tag the merged list offered a Hand in button that could only ever 404.
    const tag = (rows: any[], source: 'homework' | 'assessment') =>
      rows.map((row) => ({ ...row, __source: source }));

    return {
      homework: [
        ...tag(homework, 'homework'),
        ...tag(assessments.filter(isHomework), 'assessment'),
      ],
      otherAssessments: assessments.filter((row) => !isHomework(row)),
    };
  },
  getAttachment: (url: string) => apiClient.get(url),

  // ---- handing in --------------------------------------------------------
  // Homework used to be read-only here; these are the parent-facing half.
  // enrollmentId must be selectedStudent.enrollmentId (StudentEnrollment.Id),
  // NOT the student profile id - HomeworkSubmission.StudentId points at the
  // enrolment. The server checks it belongs to the signed-in parent regardless.

  // formData carries HomeworkId, EnrollmentId, optional SubmissionText and zero
  // or more repeated 'Files' entries, matching List<IFormFile> model binding.
  // Content-Type is left undefined so the boundary is generated for us - same
  // pattern as assignmentAPI.submitAssignment below.
  submitHomework: (formData: FormData) =>
    apiClient.post('/HomeworkSubmission/submit', formData, {
      headers: { 'Content-Type': undefined as any },
    }),

  // Returns null when nothing has been handed in yet - that is not an error.
  getMySubmission: (homeworkId: string, enrollmentId: string) =>
    apiClient.get(`/HomeworkSubmission/mine/${homeworkId}/${enrollmentId}`),
};

// "Assignments" is a separate, submission-capable module from the read-only Homework
// feature above. enrollmentId must be selectedStudent.enrollmentId (StudentEnrollment.Id),
// NOT the student's profile id — the backend uses it to identify which enrollment/attempt
// history to check.
export const assignmentAPI = {
  getAssignmentsByClass: (classId: string, enrollmentId: string) =>
    apiClient.get(`/AssignmentSubmission/student/class/${classId}`, {
      params: { enrollmentId },
    }),

  getAssignmentDetail: (assignmentId: string, enrollmentId: string) =>
    apiClient.get(`/AssignmentSubmission/student/${assignmentId}/detail`, {
      params: { enrollmentId },
    }),

  // formData must contain AssignmentId, EnrollmentId, optional SubmissionText, and zero or
  // more repeated 'Files' entries — matches ASP.NET Core's List<IFormFile> model binding.
  // Content-Type is left undefined so axios/the browser can auto-generate the multipart
  // boundary, same pattern as studentAPI.uploadStudentDocument above.
  submitAssignment: (formData: FormData) =>
    apiClient.post('/AssignmentSubmission/submit', formData, {
      headers: { 'Content-Type': undefined as any },
    }),

  // Relative blob paths (teacher attachment fileUrl / submittedFiles[].fileUrl) resolve
  // through this same "fetch base64 + contentType + fileName" mechanism as
  // homeworkAPI.getAttachment — reused here for consistency, not a direct public URL.
  getAttachment: (url: string) => apiClient.get(url),
};

// "Projects" mirrors the web app's Project feature. studentId must be
// selectedStudent.enrollmentId (StudentEnrollment.Id), same convention as assignmentAPI's
// enrollmentId — the backend keys ProjectSubmission.StudentId off the enrollment, not the
// student profile id. Passing studentId to getProjectsByClass also populates each item's
// my* fields (status/grade), matching the web app's ParentProjectTable/ParentProjectDetail.
export const projectAPI = {
  getProjectsByClass: (classId: string, studentId: string) =>
    apiClient.get(`/Project/parent/class/${classId}`, {
      params: { studentId },
    }),

  // formData must contain projectId, studentId, optional submissionText, and optional
  // FileUrl — matches ASP.NET Core's ProjectSubmissionDto model binding. Content-Type is
  // left undefined so axios/the browser can auto-generate the multipart boundary, same
  // pattern as assignmentAPI.submitAssignment above.
  submitProject: (formData: FormData) =>
    apiClient.post('/ProjectSubmission/submit', formData, {
      headers: { 'Content-Type': undefined as any },
    }),

  // Reused for both the teacher's project attachment (/Project/download/attachment/{id})
  // and a student's own submitted file (/ProjectSubmission/download/{submissionId}) — same
  // "fetch base64 + contentType + fileName" mechanism as homeworkAPI/assignmentAPI.getAttachment.
  getAttachment: (url: string) => apiClient.get(url),
};

// The real parent notification feed (comms.CommunicationRecipient rows served
// by the communication engine). GET /Notifications/Parent returns
// { items, totalCount, unreadCount, page, pageSize }; item ids here are the
// RECIPIENT ids the read/acknowledge endpoints expect.
export const notificationAPI = {

  /**
   * The parent's full history — circulars, email AND push — for the last three
   * months, newest first.
   *
   * Separate from getNotifications, which returns circulars only and is the one
   * with read state, bodies and action links. This adds the email and push
   * RECEIPTS that feed has no knowledge of: a homework email or a push that was
   * sent while the app was shut left no trace on this screen before.
   *
   * Takes no user parameter by design — the server reads the caller's id from the
   * token, so one parent cannot request another family's history.
   */
  getMyHistory: (page: number = 1, pageSize: number = 50) =>
    apiClient.get('/Notifications/my', { params: { page, pageSize } }),
  getNotifications: (page: number = 1, pageSize: number = 20) =>
    apiClient.get('/Notifications/Parent', { params: { page, pageSize } }),

  // notificationId = the feed item's recipientId
  markAsRead: (notificationId: string) =>
    apiClient.put(`/Notifications/${notificationId}/Read`),

  markAllAsRead: () => apiClient.put('/Notifications/Parent/ReadAll'),

  // The [Acknowledge] button for RequiresAcknowledgement messages; the server
  // stores who confirmed, when, and on what device.
  acknowledge: (notificationId: string, device?: string) =>
    apiClient.post(`/Notifications/${notificationId}/Acknowledge`, { device: device ?? null }),

  // Device registration stays on the existing AppNotification endpoint.
  registerFCMToken: (token: string, platform: string) =>
    apiClient.post('/AppNotification/register', { fcmToken: token, platform }),

  // Profile → Notification Preferences: category × channel matrix.
  // EMERGENCY comes back isLocked and the server ignores changes to it.
  getPreferences: () => apiClient.get('/Notifications/Preferences'),
  savePreferences: (preferences: any[]) => apiClient.put('/Notifications/Preferences', preferences),
};

// Parent-teacher meetings: open slots for my children's classes, my bookings,
// booking/cancelling, and the booked slot's calendar data (.ics + Google link).
export const meetingsAPI = {
  getAvailable: () => apiClient.get('/ParentMeetings/Available'),

  getMyBookings: () => apiClient.get('/ParentMeetings/MyBookings'),

  book: (slotId: string, studentProfileId: string) =>
    apiClient.post(`/ParentMeetings/Slots/${slotId}/Book`, { studentProfileId }),

  cancelBooking: (slotId: string) =>
    apiClient.delete(`/ParentMeetings/Slots/${slotId}/Book`),

  getCalendar: (slotId: string) =>
    apiClient.get(`/ParentMeetings/Slots/${slotId}/Calendar`),
};

export const userAPI = {
  // Get the currently authenticated user's profile, role, and permissions
  // Uses explicit Bearer token (like frontend implementation)
  getMeWithToken: async (accessToken: string) => {
    try {
      console.log('🔍 [API] Calling /api/User/me endpoint with explicit Bearer token...');
      console.log('🔐 [API] Token details:', {
        hasToken: !!accessToken,
        tokenLength: accessToken?.length ?? 0,
        tokenPrefix: accessToken?.substring(0, 20) ?? 'none'
      });

      const response = await apiClient.get('/User/me', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      console.log('✅ [API] /User/me FULL RESPONSE:', JSON.stringify(response.data, null, 2));
      console.log('✅ [API] /User/me extracted fields:', {
        status: response.status,
        userId: response.data?.UserId,
        email: response.data?.Email,
        isEmployee: response.data?.IsEmployee,
        permissions: response.data?.Permissions,
        globalRole: response.data?.GlobalRole,
        moduleRole: response.data?.ModuleRole,
        hasWebAccess: response.data?.HasWebAccess,
        hasMobileAccess: response.data?.HasMobileAccess,
      });
      return response;
    } catch (error: any) {
      console.error('❌ [API] /User/me endpoint FAILED:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        data: error.response?.data,
      });
      throw error;
    }
  },

  // Legacy method using axios interceptor
  getMe: async () => {
    try {
      console.log('🔍 [API] Calling /api/User/me endpoint (interceptor method)...');
      const response = await apiClient.get('/User/me');
      console.log('✅ [API] /User/me FULL RESPONSE:', JSON.stringify(response.data, null, 2));
      return response;
    } catch (error: any) {
      console.error('❌ [API] /User/me endpoint FAILED:', error);
      throw error;
    }
  },
};

// The inbox is the parent feed, first page.
export const inboxAPI = {
  getInbox: async (_userId: string) => {
    const response = await apiClient.get('/Notifications/Parent', { params: { page: 1, pageSize: 20 } });
    return { ...response, data: response.data?.items ?? [] };
  },
};

export const examAPI = {
  getExamTypes: () =>
    apiClient.get('/ExamType'),

  /**
   * Only the exam types with a PUBLISHED result for this child's enrolment -
   * what the Exam Results picker offers a family. (getExamTypes lists every
   * exam type in the school, published or not.)
   */
  getPublishedExamTypes: (enrollmentId: string) =>
    apiClient.get('/Parent/exam/published-types', { params: { enrollmentId } }),

  getReportCard: (enrollmentId: string, academicYearId: number, examTypeIds?: string[]) =>
    apiClient.get('/ExamMarks/report/card', {
      params: { enrollmentId, academicYearId, examTypeIds },
      paramsSerializer: {
        indexes: null
      }
    }),

  getParentExamSchedule: (classId: string, academicYear: string, examTypeId?: string, examId?: string) =>
    apiClient.get(`/Parent/exam/schedule/class/${classId}`, {
      params: { academicYear, examTypeId, examId },
    }),
};

// Published report cards (per report-card configuration, e.g. "Fall 2026"). Only
// PUBLISHED cards are returned, scoped by the server to the caller's own child.
// The PDF is rendered by the server - the app never builds its own for these.
export const reportCardAPI = {
  list: (enrollmentId: string) =>
    apiClient.get<ReportCardListItem[]>('/Parent/report-cards', { params: { enrollmentId } }),

  get: (id: string) =>
    apiClient.get<ReportCardDocument>(`/Parent/report-cards/${id}`),

  // Blob, like the other binary downloads here (documentsAPI.download); an error
  // body arrives as a Blob too and is read back by describeReportCardError.
  pdf: (id: string) =>
    apiClient.get(`/Parent/report-cards/${id}/pdf`, {
      responseType: 'blob',
      headers: { Accept: 'application/pdf, application/json' },
    }),
};

// New Assessment module (separate from the legacy Exams/ExamResult APIs above) —
// same endpoints the staff web frontend's parent Assessment Results page uses.
export const assessmentAPI = {
  getStudentResults: (studentId: string, academicYear?: number) =>
    apiClient.get(`/AssessmentResults/student/${studentId}`, {
      params: academicYear ? { academicYear } : undefined,
    }),

  // Work ASSIGNED through the teacher's New Assessment form, as opposed to
  // results already published. Until this existed, an assessment was invisible
  // to a family until its marks came out - by which time knowing it had been
  // set was no longer useful.
  //
  // Each row carries isHomework, decided server-side, so both this app and its
  // web build file a row under the same tab.
  getAssignedByClass: (classId: string, startDate?: string, endDate?: string, enrollmentId?: string) =>
    apiClient.get(`/Assessment/parent/class/${classId}`, {
      params: { startDate, endDate, enrollmentId },
    }),

  // ---- handing in --------------------------------------------------------
  // The assessment counterpart of homeworkAPI's three submission routes. Same
  // request and response shapes on purpose, so one component renders both.
  //
  // enrollmentId must be selectedStudent.enrollmentId (StudentEnrollment.Id):
  // AssessmentSubmission.StudentId points at the enrolment, exactly as
  // HomeworkSubmission.StudentId does. The server re-checks it belongs to the
  // signed-in family regardless.

  /** formData carries AssessmentId, EnrollmentId, optional SubmissionText, and 'Files' parts. */
  submitAssessment: (formData: FormData) =>
    apiClient.post('/AssessmentSubmission/submit', formData, {
      headers: { 'Content-Type': undefined as any },
    }),

  /** Returns null when nothing has been handed in yet - that is not an error. */
  getMyAssessmentSubmission: (assessmentId: string, enrollmentId: string) =>
    apiClient.get(`/AssessmentSubmission/mine/${assessmentId}/${enrollmentId}`),

  getStudentMastery: (studentId: string) =>
    apiClient.get(`/OutcomeMastery/student/${studentId}`),
};

// Backend has no receipt-list/download endpoint — that action stays unavailable
// in the UI until the backend adds one.
export const feeAPI = {
  // Counter receipts for THIS guardian's children - scoped server-side by who
  // is signed in, so no student id travels in the request.
  getReceipts: () => apiClient.get('/Collection/my/receipts'),

  // The full receipt document, for rendering and downloading as a PDF.
  getReceipt: (receiptNumber: string) =>
    apiClient.get('/Collection/my/receipts/' + encodeURIComponent(receiptNumber)),

  // This is the exact endpoint the proven staff web frontend (src/frontend's
  // StudentFees.tsx) uses to render the fee table — a flat StudentInvoiceListItemDto[]
  // with a real per-row InvoiceId, which is what enables real "Pay Now".
  getStudentInvoices: (studentId: string) =>
    apiClient.get(`/StudentApplication/${studentId}/invoices`),

  getFeeBreakdown: (studentId: string) =>
    apiClient.get(`/studentfee/breakdown/${studentId}`),

  /**
   * What the parent answered about siblings on the fee screen.
   *
   * This is a CLAIM and nothing more - it reduces nobody's fees on its own. The
   * school checks it against their records, an admin approves, and an accountant
   * identifies the actual pupils before any invoice moves. The wording shown to
   * the parent says exactly that, so nobody expects a cheaper bill tomorrow.
   */
  declareSiblings: (
    studentId: string,
    body: {
      hasSibling: boolean;
      siblingCount?: number | null;
      paymentFrequency?: string | null;
      academicYear?: string | null;
      siblings: Array<{ name: string; classId?: string | null }>;
    },
  ) => apiClient.post(`/SiblingDiscount/declare/${studentId}`, body),

  // Name suggestions for the sibling field: 3+ letters, up to 5 currently-enrolled
  // pupils, name and class only. studentId is the parent's own child (the gate).
  // Returns SiblingSearchResult[]: name, classId, className.
  searchSiblings: (studentId: string, q: string) =>
    apiClient.get(`/SiblingDiscount/sibling-search/${studentId}`, { params: { q } }),

  payInvoice: (
    studentId: string,
    invoiceId: string,
    data: {
      amount: number;
      paymentMode: string;
      paymentType?: string;
      description?: string;
      statusCode: string;
      transactionId?: string;
    },
  ) =>
    apiClient.post(`/studentfee/${studentId}/invoice/${invoiceId}/payment`, {
      Amount: data.amount,
      PaymentMode: data.paymentMode,
      PaymentType: data.paymentType ?? null,
      Description: data.description ?? null,
      StatusCode: data.statusCode,
      TransactionDate: new Date().toISOString(),
      TransactionId: data.transactionId ?? null,
    }),

  // Same endpoints the staff web frontend uses for the parent's onboarding
  // "select a fee structure" popup (src/frontend's FeeStructurePopup.tsx).
  getClassFeeStructures: (classId: string, academicYear: number) =>
    apiClient.get(`/student/class/fee/structures`, {
      params: { classId, academicYear },
    }),

  updateFeeSelection: (
    enrollmentId: string,
    payload: { academicFeeStructureId: string; paymentFrequency: string; amount: number },
  ) =>
    apiClient.patch(`/StudentResult/${enrollmentId}/fee/selection`, payload),
};

export const timetableAPI = {
  // Endpoint: /StudentTimeTable/class/{classId}/timetable?date={date}&view={view}
  // Uses the same endpoint as frontend for consistency
  // View options: 'daily' | 'weekly' | 'monthly'
  getTimetable: (classId: string, date: string, view: 'daily' | 'weekly' | 'monthly' = 'weekly') =>
    apiClient.get(`/StudentTimeTable/class/${classId}/timetable`, {
      params: { date, view },
    }),
};

// No Transport/Bus controller exists in the backend at all — kept as a local
// mock module, not wired to the network.
// Transport is addressed BY CHILD, never by bus or journey: the server proves
// the guardian relationship for the child id it is given and answers 403
// otherwise, so there is no endpoint here that could be re-pointed at another
// family. The old stubs returned nulls and the screen invented the rest; these
// are the real Phase 1 endpoints.
export const transportAPI = {
  /** Everything the child journey screen needs, in one read. */
  getChildTransportToday: (studentId: string) =>
    apiClient.get<ParentTransportToday>(`/v1/parent/children/${studentId}/transport/today`),

  /** One compact card per child, for the home screen. */
  getTransportCards: () => apiClient.get<ParentTransportCard[]>('/v1/parent/transport/cards'),
};

// Backend has no schoolId-scoped route or separate media-gallery endpoint — the
// "gallery" view is derived client-side from events that carry a FileUrl.
export const eventsAPI = {
  getEvents: () => apiClient.get('/event'),

  downloadEventImage: (eventId: string) =>
    apiClient.get(`/Event/download/image/${eventId}`),

  getEventImageUrl: (eventId: string) =>
    `${BASE_URL}/Event/download/image/${eventId}`,
};

// Same endpoints the staff web frontend uses for the class gallery
// (src/frontend's ClassGallery.tsx / galleryService.ts). Read-only here —
// upload/delete are teacher-only actions and stay out of the parent app.
export const galleryAPI = {
  getGalleriesByClass: (classId: string) =>
    apiClient.get(`/GalleryImage/class/${classId}`),

  downloadGalleryImage: (galleryId: string) =>
    apiClient.get(`/GalleryImage/${galleryId}/download`),
};

// Same endpoint the staff web frontend uses on the student profile page
// (src/frontend's useAchievements.ts / achievementService.ts). Read-only —
// upsert/delete are teacher-only actions and stay out of the parent app.
export const achievementAPI = {
  getStudentAchievements: (studentId: string) =>
    apiClient.get(`/Student/${studentId}/achievements`),
};

// No LiveClass controller exists in the backend at all — kept as a local mock
// module, not wired to the network (LiveClassScreen already falls back to its
// own MOCK_LIVE constant when this returns empty).
export const liveClassAPI = {
  getLiveSessions: (_studentId: string) => Promise.resolve({ data: [] }),
  getRecordings: (_studentId: string, _days: number = 7) => Promise.resolve({ data: [] }),
  joinSession: (_sessionId: string) => Promise.resolve({ data: {} }),
};

// Same endpoint the staff web Class Dashboard uses to resolve a class's class
// teacher: a school-wide list of teacher -> class/subject assignments. Each
// entry's EmployeeId is the Employee table's own PK — NOT the identity user id
// Chat needs (GET /student/class/{id} carries no identity id at all, only
// teacherName strings). Resolve the real identity id via employeeAPI.getById below.
export const classAPI = {
  getTeacherAssignments: () => apiClient.get('/student/class/subjects/teacher'),
};

// Employee.Id -> Employee.UserId (the identity user id Chat's senderUserId /
// receiverUserId columns actually store). Employee.Id and Employee.UserId are
// distinct columns in this backend, so this lookup is required, not optional.
export const employeeAPI = {
  getById: (employeeId: string) => apiClient.get(`/Employee/${employeeId}`),
};

// {UserId} here is the child's Identity user id (StudentRelative.UserId), not Student.Id.
export const chatAPI = {
  getMessages: (receiverUserId: string) =>
    apiClient.get(`/chat/messages/receiver/${receiverUserId}`),

  sendMessage: (receiverUserId: string, text: string) =>
    apiClient.post('/chat/send', { ReceiverUserId: receiverUserId, MessageText: text }),

  getUnreadCount: () => apiClient.get('/chat/unread/count'),

  markRead: (receiverUserId: string) =>
    apiClient.put(`/chat/mark/read/${receiverUserId}`),
};

// Every teacher a parent may message about a class, in one authorized call - see
// useClassTeachers.ts, which replaces the old classAPI/employeeAPI two-hop dance above.
export const parentCommunicationAPI = {
  getClassTeachers: (classId: string) => apiClient.get(`/ParentCommunication/${classId}/teachers`),
};

// Backend has no complaint-detail-by-id or reply endpoint — the detail view reuses
// the already-fetched list item, and reply is not available until the backend adds it.
export const complaintsAPI = {
  getComplaints: (studentId: string) =>
    apiClient.get(`/Parent/student/${studentId}/complaints`),

  submitComplaint: (data: {
    raisedByUserId: string;
    studentId: string;
    assignedClassId?: string;
    description: string;
  }) =>
    apiClient.post('/Parent/raise/concern', {
      RaisedByUserId: data.raisedByUserId,
      StudentId: data.studentId,
      AssignedClassId: data.assignedClassId ?? null,
      Description: data.description,
    }),

  // A concern's two-way conversation. One set of routes for both sides - the
  // server works out which side the caller is on from the signed-in user, so
  // nothing here has to (or is allowed to) say who it is.
  //
  // Every write returns the refreshed thread, so the screen never re-fetches to
  // stay in step.
  getThread: (complaintId: string) =>
    apiClient.get(`/ComplaintConversation/${complaintId}/thread`),

  postMessage: (complaintId: string, message: string) =>
    apiClient.post(`/ComplaintConversation/${complaintId}/messages`, { message }),

  // Signs the family's side off. This does NOT close the concern on its own: it
  // stays open, as AWAITING_SCHOOL, until the school confirms too.
  resolveMySide: (complaintId: string, resolveNotes?: string) =>
    apiClient.post(`/ComplaintConversation/${complaintId}/resolve`, {
      resolveNotes: resolveNotes?.trim() ? resolveNotes.trim() : null,
    }),

  withdrawMyResolution: (complaintId: string) =>
    apiClient.post(`/ComplaintConversation/${complaintId}/withdraw-resolution`, {}),
};

// The school store: parents order from the inventory catalog. Stock is held
// for 48 hours; unpaid orders expire and the items go back to stock. Payment
// happens at the school office (online payment comes later).
export const storeAPI = {
  getProducts: () => apiClient.get('/Store/products'),

  placeOrder: (payload: {
    items: { productId: string; quantity: number; size?: string | null }[];
    studentProfileId?: string | null;
    notes?: string | null;
  }) =>
    apiClient.post('/Store/orders', {
      Items: payload.items.map(i => ({
        ProductId: i.productId,
        Quantity: i.quantity,
        Size: i.size ?? null,
      })),
      StudentProfileId: payload.studentProfileId ?? null,
      Notes: payload.notes ?? null,
    }),

  getMyOrders: () => apiClient.get('/Store/orders/my'),

  cancelOrder: (orderId: string) => apiClient.post(`/Store/orders/${orderId}/cancel`, {}),

  // Product photos are served through the authed API, so they are fetched as
  // blobs and rendered from data URLs (works on web and native alike).
  getProductImageBlob: (productId: string, imageId: string) =>
    apiClient.get(`/Inventory/products/${productId}/images/${imageId}`, { responseType: 'blob' }),
};

// "Contact Admin" tile on the dashboard: the message is emailed to the admin
// address the school configured under Notification Settings.
export const contactAdminAPI = {
  send: (subject: string, message: string) =>
    apiClient.post('/Parent/contact-admin', { Subject: subject, Message: message }),
};

// ==========================================
// PREVIOUSLY MOCKED / ORIGINAL API ENDPOINTS
// (Preserved as requested)
// ==========================================
/*
export const authAPI = {
  login: (email: string, password: string) =>
    apiClient.post('/Auth/ParentLogin', { email, password }),

  refreshToken: (refreshToken: string) =>
    apiClient.post('/Auth/RefreshToken', { refreshToken }),

  logout: () => apiClient.post('/Auth/Logout'),

  forgotPassword: (email: string) =>
    apiClient.post('/Auth/ForgotPassword', { email }),
};

// ===========================================================================
// TODO(cleanup): these `*_legacy` blocks are dead duplicates — delete them.
//
// Every one below repeats an exported const of the same name minus the suffix,
// defined earlier in this file, mostly byte for byte. Nothing imports them:
//
//     grep -rn "API_legacy" src/          # returns only these definitions
//
// They are not harmless. Editing this file means every search for a method name
// returns two hits, and it is not obvious which one is live — the routes are
// identical, so picking wrong compiles, passes tests, and silently changes
// nothing. That has already cost real time.
//
// To remove: confirm the grep above still finds no usages, delete from here to
// the end of the legacy section, then `npx tsc --noEmit` and `npx jest`. No
// runtime behaviour should change; if something breaks, it was importing a
// legacy block and should be pointed at the live one instead.
//
// Left in place for now only because deleting ~200 lines was not what was asked
// for while notification work was in flight.
// ===========================================================================

export const studentAPI_legacy = {
  getMyChildren: () => apiClient.get('/Parent/Children'),
  getStudentProfile: (studentId: string) =>
    apiClient.get(`/Student/${studentId}/Profile`),
};

export const attendanceAPI_legacy = {
  getMonthlyAttendance: (studentId: string, month: number, year: number) =>
    apiClient.get(`/Attendance/Student/${studentId}`, { params: { month, year } }),

  getAttendanceSummary: (studentId: string, month: number, year: number) =>
    apiClient.get(`/Attendance/Student/${studentId}/Summary`, {
      params: { month, year },
    }),
};

export const leaveAPI_legacy = {
  getLeaveRequests: (studentId: string) =>
    apiClient.get(`/Leave/Student/${studentId}`),

  applyLeave: (data: {
    studentId: string;
    fromDate: string;
    toDate: string;
    reason: string;
    leaveType: string;
  }) => apiClient.post('/Leave/Apply', data),

  cancelLeave: (leaveId: string) =>
    apiClient.put(`/Leave/${leaveId}/Cancel`),
};

export const homeworkAPI_legacy = {
  getHomework: (studentId: string) =>
    apiClient.get(`/Homework/Student/${studentId}`),

  getPendingHomework: (studentId: string) =>
    apiClient.get(`/Homework/Student/${studentId}/Pending`),
};

export const notificationAPI_legacy = {
  getNotifications: (page: number = 1, pageSize: number = 20) =>
    apiClient.get('/Notifications/Parent', { params: { page, pageSize } }),

  markAsRead: (notificationId: string) =>
    apiClient.put(`/Notifications/${notificationId}/Read`),

  markAllAsRead: () => apiClient.put('/Notifications/Parent/ReadAll'),

  registerFCMToken: (token: string, deviceId: string) =>
    apiClient.post('/Notifications/RegisterDevice', {
      fcmToken: token,
      deviceId,
      platform: 'mobile',
    }),
};

export const examAPI_legacy = {
  getResults: (studentId: string) =>
    apiClient.get(`/Exam/Student/${studentId}/Results`),
};

export const feeAPI_legacy = {
  getFeeDetails: (studentId: string) =>
    apiClient.get(`/Fee/Student/${studentId}`),

  getInstallments: (studentId: string) =>
    apiClient.get(`/Fee/Student/${studentId}/Installments`),

  payInstallment: (
    installmentId: string,
    paymentMode: string,
    chequeOrDDNumber?: string,
    bankName?: string,
  ) =>
    apiClient.post(`/Fee/Installment/${installmentId}/Pay`, {
      paymentMode,
      chequeOrDDNumber,
      bankName,
    }),

};

export const timetableAPI_legacy = {
  getTimetable: (studentId: string) =>
    apiClient.get(`/Timetable/Student/${studentId}`),
};

export const eventsAPI_legacy = {
  getEvents: (schoolId: string) =>
    apiClient.get(`/Events/School/${schoolId}`),

  getMedia: (schoolId: string, page: number = 1) =>
    apiClient.get(`/Events/School/${schoolId}/Media`, { params: { page } }),
};

export const liveClassAPI_legacy = {
  getLiveSessions: (studentId: string) =>
    apiClient.get(`/LiveClass/Student/${studentId}/Live`),

  getRecordings: (studentId: string, days: number = 7) =>
    apiClient.get(`/LiveClass/Student/${studentId}/Recordings`, { params: { days } }),

  joinSession: (sessionId: string) =>
    apiClient.post(`/LiveClass/Session/${sessionId}/Join`),
};

export const chatAPI_legacy = {
  getMessages: (studentId: string, page: number = 1) =>
    apiClient.get(`/Chat/Parent/Student/${studentId}`, { params: { page } }),

  sendMessage: (studentId: string, text: string) =>
    apiClient.post(`/Chat/Parent/Student/${studentId}/Message`, { text }),

  markRead: (studentId: string) =>
    apiClient.put(`/Chat/Parent/Student/${studentId}/Read`),
};

export const complaintsAPI_legacy = {
  getComplaints: (studentId: string) =>
    apiClient.get(`/Complaints/Student/${studentId}`),

  getComplaintDetail: (complaintId: string) =>
    apiClient.get(`/Complaints/${complaintId}`),

  submitComplaint: (data: {
    studentId: string;
    subject: string;
    description: string;
    recipientType: string;
    priority: string;
    category: string;
  }) => apiClient.post('/Complaints', data),

  replyToComplaint: (complaintId: string, message: string) =>
    apiClient.post(`/Complaints/${complaintId}/Reply`, { message }),
};
*/

export default apiClient;
